
import React, { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Bell, Check, Trash2 } from "lucide-react";
import PatientHeader from '@/ui/shared-components/patient/Header';
import PatientBottomNav from '@/ui/shared-components/patient/BottomNav';
import { formatDistanceToNow } from 'date-fns';

// Sample notification data (would come from API in real app)
const sampleNotifications = [
  {
    id: 1,
    title: "New Treatment Plan",
    message: "Dr. James Wilson has created a new treatment plan for your dental case.",
    date: new Date(2025, 3, 10, 14, 30),
    read: false,
    category: "treatment"
  },
  {
    id: 2,
    title: "Appointment Reminder",
    message: "Your appointment with Dr. Lisa Chen is tomorrow at 2:00 PM.",
    date: new Date(2025, 3, 9, 10, 15),
    read: true,
    category: "appointment"
  },
  {
    id: 3,
    title: "Document Uploaded",
    message: "Your X-ray results have been uploaded to your medical documents.",
    date: new Date(2025, 3, 8, 16, 45),
    read: false,
    category: "document"
  },
  {
    id: 4,
    title: "Travel Confirmation",
    message: "Your flight to Istanbul has been confirmed for April 15, 2025.",
    date: new Date(2025, 3, 5, 9, 20),
    read: true,
    category: "travel"
  },
  {
    id: 5,
    title: "Message from Doctor",
    message: "Dr. Robert Smith has sent you a message regarding your follow-up care.",
    date: new Date(2025, 3, 3, 11, 10),
    read: false,
    category: "message"
  }
];

interface Notification {
  id: number;
  title: string;
  message: string;
  date: Date;
  read: boolean;
  category: string;
}

const NotificationItem = ({ 
  notification, 
  onMarkRead, 
  onDelete 
}: { 
  notification: Notification; 
  onMarkRead: (id: number) => void; 
  onDelete: (id: number) => void;
}) => {
  return (
    <Card className={`mb-3 ${!notification.read ? 'border-l-4 border-l-primary' : ''}`}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="font-medium">{notification.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
            <p className="text-xs text-muted-foreground mt-2">
              {formatDistanceToNow(notification.date, { addSuffix: true })}
            </p>
          </div>
          <div className="flex gap-2">
            {!notification.read && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => onMarkRead(notification.id)}
                title="Mark as read"
              >
                <Check className="h-4 w-4 text-primary" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={() => onDelete(notification.id)}
              title="Delete notification"
            >
              <Trash2 className="h-4 w-4 text-primary" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Notifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>(sampleNotifications);
  const { toast } = useToast();

  const handleMarkAsRead = (id: number) => {
    setNotifications(prevNotifications => 
      prevNotifications.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
    toast({
      title: "Notification marked as read",
    });
  };

  const handleDelete = (id: number) => {
    setNotifications(prevNotifications => 
      prevNotifications.filter(notification => notification.id !== id)
    );
    toast({
      title: "Notification deleted",
    });
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prevNotifications => 
      prevNotifications.map(notification => ({ ...notification, read: true }))
    );
    toast({
      title: "All notifications marked as read",
    });
  };

  // Filter notifications by read status
  const unreadNotifications = notifications.filter(notification => !notification.read);
  const readNotifications = notifications.filter(notification => notification.read);

  return (
    <div className="min-h-screen bg-muted/30 pb-20 pt-16">
      <PatientHeader />
      
      <div className="container mx-auto px-4 pt-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-bold">Notifications</h1>
          </div>
          {unreadNotifications.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleMarkAllAsRead}
            >
              Mark all as read
            </Button>
          )}
        </div>
        
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="all">
              All
              {notifications.length > 0 && <span className="ml-2 text-xs bg-muted rounded-full px-2 py-0.5">{notifications.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="unread">
              Unread
              {unreadNotifications.length > 0 && <span className="ml-2 text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5">{unreadNotifications.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="read">Read</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all">
            {notifications.length > 0 ? (
              <div>
                {notifications.map((notification) => (
                  <NotificationItem 
                    key={notification.id}
                    notification={notification}
                    onMarkRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No notifications</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="unread">
            {unreadNotifications.length > 0 ? (
              <div>
                {unreadNotifications.map((notification) => (
                  <NotificationItem 
                    key={notification.id}
                    notification={notification}
                    onMarkRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Check className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No unread notifications</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="read">
            {readNotifications.length > 0 ? (
              <div>
                {readNotifications.map((notification) => (
                  <NotificationItem 
                    key={notification.id}
                    notification={notification}
                    onMarkRead={handleMarkAsRead}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No read notifications</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      <PatientBottomNav />
    </div>
  );
};

export default Notifications;
